import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { PageHeader, useFormats } from '@/pages/bacen/shared';
import { bacenEntityParams, fetchBacenEntities } from '@/services/bacen';

const PAGE_SIZE = 50;

/**
 * The company directory: every CNPJ the ledger names, alphabetical, never
 * ordered by a count. Persons are not here, by decision -- the directory
 * says how many rows name one and lists none of them. `in_registry` is the
 * current Instituicoes em Funcionamento registry, a snapshot that omits
 * liquidated institutions and most declarants: an axis, never a filter on
 * what counts as a fined company.
 */
export function BacenEntitiesPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, percent } = useFormats();
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('query') ?? '');
  const [query, setQuery] = useState<string | null>(seed.get('query') || null);
  const [inRegistry, setInRegistry] = useState(seed.get('in_registry') ?? '');
  const filters = {
    query,
    in_registry: inRegistry === '' ? null : inRegistry === 'true',
  };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['bacen', 'entities', filters],
    queryFn: ({ pageParam }) =>
      fetchBacenEntities({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        page="bacen:entities"
        exportPath={`/api/v1/bacen/export/entities?${bacenEntityParams(filters).toString()}`}
      />

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="bacen-entity-query" className="text-xs font-medium text-foreground">
            {t('bacen:entities.search.label')}
          </label>
          <input
            id="bacen-entity-query"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('bacen:entities.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <div className="space-y-1">
          <label
            htmlFor="bacen-entity-registry"
            className="block text-xs font-medium text-foreground"
          >
            {t('bacen:entities.search.inRegistry')}
          </label>
          <select
            id="bacen-entity-registry"
            value={inRegistry}
            onChange={(event) => setInRegistry(event.target.value)}
            className="rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('bacen:cases.filters.any')}</option>
            <option value="true">{t('bacen:cases.filters.inRegistry_yes')}</option>
            <option value="false">{t('bacen:cases.filters.inRegistry_no')}</option>
          </select>
        </div>
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('bacen:entities.search.submit')}
        </button>
      </form>

      {data ? (
        <section className="rounded-lg border bg-card p-4" data-directory>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            {t('bacen:entities.shape.title')}
          </h2>
          <Answer answer={data.directory} help={t('bacen:entities.shape.help')}>
            {(d) => (
              <div className="space-y-2">
                <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                  {(
                    [
                      ['entities', number.format(d.entities)],
                      ['withTwoOrMore', number.format(d.with_two_or_more)],
                      [
                        'inRegistry',
                        `${number.format(d.in_registry)} (${percent.format(d.registry_share)})`,
                      ],
                      ['personRows', number.format(d.person_rows)],
                    ] as const
                  ).map(([key, value]) => (
                    <div key={key} className="rounded-md border bg-muted/30 p-2" data-shape={key}>
                      <dt className="text-xs text-muted-foreground">
                        {t(`bacen:entities.shape.${key}`)}
                      </dt>
                      <dd className="font-semibold tabular-nums text-foreground">{value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="text-xs text-muted-foreground" data-registry-sets>
                  {Object.entries(d.by_registry_set)
                    .map(
                      ([set, n]) =>
                        `${t(`bacen:codes.registrySet.${set}`, { defaultValue: set })} ${number.format(n)}`,
                    )
                    .join(' · ')}
                </p>
              </div>
            )}
          </Answer>
          <div className="mt-3">
            <FieldGuide i18nKey="bacen:entities.guide.shape" />
          </div>
        </section>
      ) : null}

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <EmptyState hint={t('bacen:entities.empty')} />
      ) : (
        <section className="rounded-lg border bg-card" data-stale={isPlaceholderData || undefined}>
          <div className="px-4 py-2 text-xs text-muted-foreground">
            <span data-total={data?.total}>
              {t('bacen:entities.count', {
                loaded: number.format(items.length),
                total: number.format(data?.total ?? 0),
              })}
            </span>
          </div>
          <div ref={scrollRef} className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('bacen:entities.columns.name')}</th>
                  <th className="px-3 py-2 font-medium">{t('bacen:entities.columns.registry')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:entities.columns.rows')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:entities.columns.processes')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:entities.columns.ruled')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:entities.columns.years')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.cnpj} className="border-t align-top" data-cnpj={row.cnpj}>
                    <td className="px-3 py-2">
                      <Link
                        to={`/bacen/e/${row.cnpj}`}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {row.name}
                      </Link>
                      <div className="text-xs tabular-nums text-muted-foreground">{row.cnpj}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {row.in_registry
                        ? (row.registry_segment ?? t('bacen:entities.inRegistry'))
                        : t('bacen:entities.notInRegistry')}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-foreground">
                      {number.format(row.rows)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.processes)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.ruled)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {row.first_year ?? '—'}–{row.last_year ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} />
          </div>
          {hasNextPage ? (
            <div className="border-t px-4 py-2 text-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={isFetchingNextPage}
                className="text-sm text-primary underline-offset-2 hover:underline"
              >
                {isFetchingNextPage ? t('common:state.loading') : t('bacen:entities.loadMore')}
              </button>
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}
